import { ConfigurationChange } from '@dto/configurationChanges.dto';
import { Export } from '@dto/pageResponse.dto';
import { DateUtil } from "@dto/util/DateUtil";
import { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig } from 'antd/es/table/interface';
import dayjs, { Dayjs } from "dayjs";
import { FC, ReactNode, useEffect, useState } from 'react';
import XmRangeDatePicker from 'src/component/dataTimePicker/XmRangeDatePicker';
import {
  GridScope
} from 'src/component/grid/Grid';
import { ColProps, IGridEntry } from "src/component/grid/gridTypes";
import { XmHelp } from 'src/component/help/XmHelp';
import XmTableUtil from 'src/component/tableUtil/XmTableUtil';
import { Const } from 'src/data/Const';
import { ComponentProps } from './CFGTracker';

const DATETIME_FORMAT = `${DateUtil.DATETIME_FORMAT}:ss`;

interface Props extends ComponentProps {
  children?: ReactNode;
}

export const CFGTrackerTable: FC<Props> = ({ items, onRangeChange, showTechnologyColumn, range, isLoading, children }) => {
  const [gs] = useState(() => new GridScope<ConfigurationChange>());
  const [divNode, setDivNode] = useState<HTMLDivElement>();
  const columnsFiltered = showTechnologyColumn ? columns : columns.filter(column => column.key !== 'hw_type');

  const handleDateFilterChange = (dates: Dayjs[]) => {
    if (dates?.length !== 2) {
      onRangeChange();
      return;
    }

    const [start, end] = dates;
    onRangeChange({ start, end });
  };

  const [useReset, setUseReset] = useState(false);
  const [key, setKey] = useState<string>(Math.random().toString());

  const resetFilters = (key: string) => {
    setKey(key);
    setExport({
      ...Const.DEFAULT_EXPORT,
      fileName: "cfgtracker_export",
      body: {
        headers: columnsFiltered.map(column => column.title),
        values: mapValues(items)
      }
    });
    setUseReset(false);
  };

  const mapValues = (values: ConfigurationChange[]) => {
    return values ? values.map((cfg) => {
      return showTechnologyColumn ? [
        cfg.timestamp,
        cfg.label,
        cfg.root_parent,
        cfg.hw_type,
        cfg.subsystem,
        cfg.property,
        cfg.old_value,
        cfg.new_value
      ] : [
        cfg.timestamp,
        cfg.label,
        cfg.root_parent,
        cfg.subsystem,
        cfg.property,
        cfg.old_value,
        cfg.new_value
      ];
    }) : [];
  };

  const [exportProp, setExport] = useState<Export>({
    ...Const.DEFAULT_EXPORT,
    fileName: "cfgtracker_export",
    body: {
      headers: columnsFiltered.map(column => column.title),
      values: mapValues(items)
    }
  });

  const onChange = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<ConfigurationChange & IGridEntry> | SorterResult<ConfigurationChange & IGridEntry>[], extra: TableCurrentDataSource<ConfigurationChange>) => {
    if (filters) {
      for (const key in filters) {
        if (filters[key]?.length !== null) {
          setUseReset(true);
          break;
        }
      }
    }

    setExport({
      fileName: "cfgtracker_export",
      method: "POST",
      url: "/api/exporter/v1/values",
      body: {
        headers: columnsFiltered.map(column => column.title),
        values: mapValues(extra.currentDataSource)
      }
    });
  };

  useEffect(() => {
    setExport({
      ...Const.DEFAULT_EXPORT,
      fileName: "cfgtracker_export",
      body: {
        headers: columnsFiltered.map(column => column.title),
        values: mapValues(items)
      }
    });
  }, [items]);

  return (
    <div ref={r => r ? setDivNode(r) : null} className='xm-cfg-tracker-table'>
      <div className='xm-cfg-tracker-date-tableutil-container'>
        <XmRangeDatePicker
          value={range}
          className='xm-cfg-tracker-picker table'
          showTime
          defaultValue={range}
          format={DateUtil.DATETIME_FORMAT}
          onChange={handleDateFilterChange}
        />
        <div style={{ display: "flex" }}>
          <XmTableUtil
            useReset={true}
            useExport={exportProp ? true : false}
            exportableTypes={{ PDF: false }}
            export={exportProp}
            setTableKey={resetFilters}
            size={"medium"}
            activeReset={useReset}
            style={{ width: 33 }}
          />
          <XmHelp href="https://xormon.com/CFG-Tracker.php" className="xm-help" />
        </div>
      </div>
      {children}
      {divNode && <gs.Grid
        className="xm-table-fill"
        loading={isLoading}
        style={{ minWidth: '200px' }}
        dataSource={items}
        rowKey={record => record.item_id + record.timestamp + record.property}
        columns={columnsFiltered}
        // scroll={{ x: true }}
        onChange={onChange}
        key={key}
        topOffset={180}
      />}
    </div>
  );
};

const columnProps = (dataIndex: keyof ConfigurationChange): ColProps<ConfigurationChange & IGridEntry> => ({
  dataIndex,
  key: dataIndex,
  filter: { field: [dataIndex], type: 'string' },
  sorter: (a: ConfigurationChange, b: ConfigurationChange) =>
    a[dataIndex].localeCompare(b[dataIndex]),
});

export const columns: ColProps<ConfigurationChange & IGridEntry>[] = [
  {
    title: 'Timestamp',
    dataIndex: 'timestamp',
    key: 'timestamp',
    defaultSortOrder: 'descend',
    sorter: (a: ConfigurationChange, b: ConfigurationChange) =>
      a.timestamp.localeCompare(b.timestamp),
    render: (text: Date) => dayjs(text).format(DATETIME_FORMAT),
  },
  {
    title: 'Item',
    ...columnProps('label')
  },
  {
    title: 'Root Parent',
    ...columnProps('root_parent')
  },
  {
    title: 'Technology',
    ...columnProps('hw_type')
  },
  {
    title: 'Subsystem',
    ...columnProps('subsystem')
  },
  {
    title: 'Property',
    ...columnProps('property')
  },
  {
    title: 'Old Value',
    ...columnProps('old_value')
  },
  {
    title: 'New Value',
    ...columnProps('new_value')
  },
];